import AdminPageHeader from "@/components/admin/page-header";
import { MenuImage } from "@/components/menu/menu-card";
import { useLoadFeedback } from "@/hooks/use-load-feedback";
import { PageState } from "@/components/ui/page-state";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X } from "lucide-react";
import api, { fileUrl, formatIDR, apiErrorMessage } from "@/lib/api";
import { useImageUpload, IMAGE_ACCEPT, UPLOAD_HINT } from "@/hooks/use-image-upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useOutlet } from "@/context/OutletContext";

const empty = { name: "", description: "", price: "", category_id: "", image_url: "", available: true };

export default function AdminMenu() {
  const { activeOutlet } = useOutlet();
  const outletId = activeOutlet?.id;
  const { loading, refreshing, hasLoaded, error: loadError, run } = useLoadFeedback(outletId);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingOutletId, setEditingOutletId] = useState(null);
  const [form, setForm] = useState(empty);
  const { uploading, upload } = useImageUpload();
  const loadRequestRef = useRef(0);
  const saveRequestRef = useRef(0);
  const createIdRef = useRef(null);
  const outletIdRef = useRef(outletId);
  outletIdRef.current = outletId;

  useEffect(() => {
    // Dialog dan operasi lama selalu terikat ke outlet saat dibuka. Saat outlet
    // berubah, jangan biarkan nilai/form lama tersimpan ke outlet baru.
    loadRequestRef.current += 1;
    saveRequestRef.current += 1;
    createIdRef.current = null;
    setOpen(false);
    setEditing(null);
    setEditingOutletId(null);
    setForm(empty);
    setItems([]);
  }, [outletId]);

  const load = useCallback(() => run(async () => {
    const requestId = ++loadRequestRef.current;
    if (!outletId) {
      setItems([]);
      return;
    }
    const [m, c] = await Promise.all([
      api.get("/menu-items", { params: { outlet_id: outletId } }),
      api.get("/categories"),
    ]);
    if (requestId !== loadRequestRef.current) return;
    setItems(m.data);
    setCategories(c.data);
  }), [outletId, run]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    if (!outletId) return toast.error("Pilih outlet terlebih dahulu");
    setEditing(null);
    setEditingOutletId(outletId);
    createIdRef.current = globalThis.crypto?.randomUUID?.() || `menu-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (item) => {
    if (!outletId) return toast.error("Pilih outlet terlebih dahulu");
    setEditing(item);
    setEditingOutletId(outletId);
    setForm({ ...item, price: String(item.price) });
    setOpen(true);
  };

  const save = async () => {
    const dialogOutletId = editingOutletId;
    if (!dialogOutletId || outletId !== dialogOutletId) return toast.error("Outlet berubah. Buka kembali menu untuk menyimpan.");
    if (!form.name || !form.price || !form.category_id) return toast.error("Nama, harga, kategori wajib diisi");
    const { name, description, price, category_id, image_url, available } = form;
    const payload = { name, description, price: Number(price), category_id, image_url };
    const requestId = ++saveRequestRef.current;
    try {
      if (editing) {
        const { data } = await api.put(`/menu-items/${editing.id}`, payload);
        if (requestId !== saveRequestRef.current || outletIdRef.current !== dialogOutletId) return;
        try {
          await api.patch(`/menu-items/${data.id}/availability`, { outlet_id: dialogOutletId, available });
        } catch (err) {
          if (requestId === saveRequestRef.current && outletIdRef.current === dialogOutletId) {
            toast.error(apiErrorMessage(err, "Katalog tersimpan, tetapi status ketersediaan gagal disimpan"));
          }
          return;
        }
      } else {
        await api.post("/menu-items", { ...payload, id: createIdRef.current, outlet_id: dialogOutletId, available });
      }
      if (requestId !== saveRequestRef.current || outletIdRef.current !== dialogOutletId) return;
      toast.success("Tersimpan");
      setOpen(false); setEditing(null); setEditingOutletId(null); createIdRef.current = null; setForm(empty); load();
    } catch (err) {
      if (requestId === saveRequestRef.current && outletIdRef.current === dialogOutletId) {
        toast.error(apiErrorMessage(err, "Gagal menyimpan"));
      }
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus menu ini?")) return;
    await api.delete(`/menu-items/${id}`);
    toast.success("Terhapus"); load();
  };

  const handleUpload = (e) => {
    const dialogOutletId = editingOutletId;
    upload(e, { folder: "menu", onUploaded: (id) => {
      if (dialogOutletId && outletIdRef.current === dialogOutletId) {
        setForm((f) => ({ ...f, image_url: id }));
      }
    }});
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "-";
  const imgSrc = (v) => v && v.startsWith("http") ? v : (v ? fileUrl(v) : "");

  if (loading || (loadError && !hasLoaded)) return <PageState kind={loading ? "loading" : "error"} title={loading ? "Memuat data" : "Data belum tersedia"} description={loadError} onRetry={loadError ? load : undefined} />;

  return (
    <div aria-busy={refreshing} className="admin-page space-y-6" data-testid="admin-menu-page">
      {loadError && <PageState kind="error" title="Data belum dapat diperbarui" description={loadError} onRetry={load} />}
      <AdminPageHeader title="Menu Hidangan" description={<> Kelola katalog menu · status untuk <b>{activeOutlet?.name || "outlet terpilih"}</b> </>} actions={<Button onClick={openNew} data-testid="add-menu-btn" className="rounded-full">
          <Plus className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Tambah Menu</span><span className="sm:hidden">Tambah</span>
        </Button>} />

      {/* Mobile: card list. Desktop: table */}
      <div className="grid grid-cols-1 md:hidden gap-3">
        {items.length === 0 && (
          <p className="text-center text-muted-foreground py-8 text-sm">Belum ada menu. Klik "Tambah".</p>
        )}
        {items.map((item) => (
          <div key={item.id} className="surface-card p-4 flex gap-3" data-testid={`menu-card-${item.id}`}>
            <div className="w-16 h-16 rounded-lg bg-accent overflow-hidden flex-shrink-0">
              <MenuImage item={item} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm break-words">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{catName(item.category_id)}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="font-display font-bold text-sm text-primary">{formatIDR(item.price)}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${item.available ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}>
                  {item.available ? "Tersedia" : "Nonaktif"}
                </span>
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" aria-label={`Edit ${item.name}`} onClick={() => openEdit(item)}><Pencil className="w-3.5 h-3.5" />Edit</Button>
                <Button variant="ghost" size="sm" className="w-9 p-0" aria-label={`Hapus ${item.name}`} onClick={() => remove(item.id)}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden md:block surface-card admin-table-scroll overflow-x-auto" tabIndex={0} role="region" aria-label="Daftar hidangan, geser untuk melihat semua kolom">
        <table className="w-full text-sm">
          <thead className="bg-accent/50 border-b border-border text-left">
            <tr>
              <th className="p-3 font-medium">Gambar</th>
              <th className="p-3 font-medium">Nama</th>
              <th className="p-3 font-medium">Kategori</th>
              <th className="p-3 font-medium">Harga</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Belum ada menu. Klik "Tambah Menu".</td></tr>
            )}
            {items.map((item) => (
              <tr key={item.id} className="border-b border-border last:border-0" data-testid={`menu-row-${item.id}`}>
                <td className="p-3">
                  <div className="w-12 h-12 rounded-lg bg-accent overflow-hidden">
                    <MenuImage item={item} className="w-full h-full object-cover" />
                  </div>
                </td>
                <td className="p-3">
                  <p className="font-medium">{item.name}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>
                </td>
                <td className="p-3 text-muted-foreground">{catName(item.category_id)}</td>
                <td className="p-3 font-medium">{formatIDR(item.price)}</td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${item.available ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}>
                    {item.available ? "Tersedia" : "Nonaktif"}
                  </span>
                </td>
                <td className="p-3 text-right space-x-1">
                  <Button variant="ghost" size="sm" aria-label={`Edit ${item.name}`} onClick={() => openEdit(item)} data-testid={`edit-menu-${item.id}-btn`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" aria-label={`Hapus ${item.name}`} onClick={() => remove(item.id)} data-testid={`delete-menu-${item.id}-btn`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) { setEditing(null); setEditingOutletId(null); createIdRef.current = null; setForm(empty); }
      }}>
        <DialogContent className="admin-dialog max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Menu" : "Tambah Menu"}</DialogTitle>
            <DialogDescription>Isi detail menu, harga, kategori dan gambar item.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Gambar</Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="w-20 h-20 rounded-lg bg-accent overflow-hidden border border-border relative flex-shrink-0">
                  {form.image_url && (
                    <>
                      <img src={imgSrc(form.image_url)} alt="" className="w-full h-full object-cover"/>
                      <button aria-label="Hapus gambar menu" onClick={() => setForm({...form, image_url: ""})}
                              className="absolute top-1 right-1 w-5 h-5 bg-white/90 rounded-full grid place-items-center">
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
                <label className={`flex-1 cursor-pointer ${uploading ? "pointer-events-none opacity-60" : ""}`}>
                  <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:bg-accent/30 transition-colors">
                    <Upload className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{uploading ? "Uploading..." : "Klik untuk upload gambar"}</span>
                    <span className="block text-[11px] text-muted-foreground/70 mt-0.5">{UPLOAD_HINT}</span>
                  </div>
                  <input type="file" accept={IMAGE_ACCEPT} className="sr-only" disabled={uploading} onChange={handleUpload} data-testid="menu-image-upload"/>
                </label>
              </div>
            </div>
            <div><Label htmlFor="adminmenu-field-1">Nama</Label><Input id="adminmenu-field-1" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-white" data-testid="menu-name-input"/></div>
            <div><Label htmlFor="adminmenu-field-2">Deskripsi</Label><Textarea id="adminmenu-field-2" rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-white" data-testid="menu-desc-input"/></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label htmlFor="adminmenu-field-3">Harga (Rp)</Label><Input id="adminmenu-field-3" type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} className="bg-white" data-testid="menu-price-input"/></div>
              <div>
                <Label htmlFor="adminmenu-select-5">Kategori</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({...form, category_id: v})}>
                  <SelectTrigger id="adminmenu-select-5" className="bg-white" data-testid="menu-category-select"><SelectValue placeholder="Pilih..."/></SelectTrigger>
                  <SelectContent className="admin-popup">
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="adminmenu-field-4">Tersedia di {activeOutlet?.name || "outlet ini"}</Label>
              <Switch id="adminmenu-field-4" checked={form.available} onCheckedChange={(v) => setForm({...form, available: v})} data-testid="menu-available-switch"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="save-menu-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
